import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  fetchBalanceHistory,
  fetchIncomeCategories,
  fetchExpenseCategories,
  updateIncome,
  deleteIncome,
  updateExpense,
  deleteExpense,
  fetchMonthlyBalanceSummary,
} from "../services/api";
import Header from "../components/Header";
import Layout from "../components/Layout";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Download } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import { enUS } from "date-fns/locale";

registerLocale("en", enUS);

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    const value = payload[0].value;
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 shadow-md">
        <p className="font-semibold">
          {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][label - 1]}
        </p>
        <p className="text-green-400">{currencyFormatter.format(value)}</p>
      </div>
    );
  }
  return null;
}

export default function BalanceHistoryPage() {
  // --- Core states ---
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSkeleton, setShowSkeleton] = useState(false);

  // --- Filters ---
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // --- Categories ---
  const [incomeCategories, setIncomeCategories] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [tempCategories, setTempCategories] = useState([]);

  // --- Edit handling ---
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [savingMap, setSavingMap] = useState({});
  const [errorMap, setErrorMap] = useState({});
  const [savedMap, setSavedMap] = useState({});
  const [csvLoading, setCsvLoading] = useState(false);

  // --- Graph ---
  const [year, setYear] = useState(new Date().getFullYear());
  const [monthlyTrend, setMonthlyTrend] = useState([]);

  // --- Pagination / infinite scroll ---
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);
  const PAGE_SIZE = 15;

  // --- Load monthly balance trend ---
  useEffect(() => {
    (async () => {
      try {
        const data = await fetchMonthlyBalanceSummary(year);
        setMonthlyTrend(data);
      } catch (err) {
        console.error("Trend fetch failed:", err);
      }
    })();
  }, [year]);

  // --- Load categories once ---
  useEffect(() => {
    fetchIncomeCategories()
      .then((d) => setIncomeCategories(d || []))
      .catch(() => setIncomeCategories([]));
    fetchExpenseCategories()
      .then((d) => setExpenseCategories(d || []))
      .catch(() => setExpenseCategories([]));
  }, []);

  // --- Map names to IDs ---
  const incomeNameToId = useMemo(() => {
    const map = {};
    (incomeCategories || []).forEach((c) => {
      const id = c.id_income_category ?? c.id ?? c.idIncomeCategory;
      const name = c.income_category_name ?? c.name ?? String(id);
      if (id != null && name) map[name] = String(id);
    });
    return map;
  }, [incomeCategories]);

  const expenseNameToId = useMemo(() => {
    const map = {};
    (expenseCategories || []).forEach((c) => {
      const id = c.id_expense_category ?? c.id ?? c.idExpenseCategory;
      const name = c.expense_category_name ?? c.name ?? String(id);
      if (id != null && name) map[name] = String(id);
    });
    return map;
  }, [expenseCategories]);

  // --- Helper to get categories for edit dropdown ---
  const categoriesForType = useCallback((type) => {
    if (type === "income") {
      return (incomeCategories || []).map(c => ({
        id: c.id_income_category ?? c.id ?? c.idIncomeCategory,
        name: c.income_category_name ?? c.name ?? ""
      }));
    } else {
      return (expenseCategories || []).map(c => ({
        id: c.id_expense_category ?? c.id ?? c.idExpenseCategory,
        name: c.expense_category_name ?? c.name ?? ""
      }));
    }
  }, [incomeCategories, expenseCategories]);

  // --- Fetch history with pagination ---
  const fetchAndSetHistory = useCallback(
    async (append = false, pageNumber = 0) => {
      setLoading(true);
      setShowSkeleton(false);
      const timer = setTimeout(() => setShowSkeleton(true), 1000);

      try {
        const limit = PAGE_SIZE;
        const offset = append ? pageNumber * PAGE_SIZE : 0;
        const rows = await fetchBalanceHistory(startDate, endDate, selectedCategories, limit, offset);
        if (!Array.isArray(rows)) return;

        if (append && pageNumber > 0) setHistory((prev) => [...prev, ...rows]);
        else setHistory(rows);

        if (!append) {
          const edits = {};
          (rows || []).forEach((r) => {
            if (r.transaction_id == null) return;
            const key = `${r.transaction_type}-${r.transaction_id}`;
            const amt = Math.abs(Number(r.transaction_amt ?? r.balance_amt ?? 0));
            const desc = r.description ?? "";
            let categoryId = "";
            if (r.transaction_type === "income") {
              categoryId = incomeNameToId[r.category] ?? (r.id_income_category != null ? String(r.id_income_category) : "");
            } else {
              categoryId = expenseNameToId[r.category] ?? (r.id_expense_category != null ? String(r.id_expense_category) : "");
            }
            edits[key] = { amount: amt, description: desc, categoryId };
          });
          setEditValues(edits);
        }

        setHasMore(rows.length === PAGE_SIZE);
      } catch (err) {
        console.error("History fetch error:", err);
      } finally {
        clearTimeout(timer);
        setLoading(false);
      }
    },
    [startDate, endDate, selectedCategories, incomeNameToId, expenseNameToId]
  );

  // --- Initial / filtered load ---
  useEffect(() => {
    setPage(0);
    fetchAndSetHistory(false, 0);
  }, [startDate, endDate, selectedCategories, fetchAndSetHistory]);

  // --- Infinite scroll observer ---
  useEffect(() => {
    if (startDate || endDate) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loading) setPage((p) => p + 1);
    });
    const node = loaderRef.current;
    if (node) observer.observe(node);
    return () => node && observer.unobserve(node);
  }, [hasMore, loading, startDate, endDate]);

  // --- Fetch more when page changes ---
  useEffect(() => {
    if (page > 0 && !(startDate || endDate)) fetchAndSetHistory(true, page);
  }, [page, startDate, endDate, fetchAndSetHistory]);

  // --- Category dropdown logic ---
  const toggleTempCategory = (name) => {
    setTempCategories((prev) => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  };
  const applyCategories = () => {
    setSelectedCategories(tempCategories);
    setDropdownOpen(false);
  };

  const combinedCategoryNames = useMemo(() => {
    const iNames = (incomeCategories || []).map(c => c.income_category_name ?? c.name ?? "");
    const eNames = (expenseCategories || []).map(c => c.expense_category_name ?? c.name ?? "");
    return [...new Set([...iNames.filter(Boolean), ...eNames.filter(Boolean)])];
  }, [incomeCategories, expenseCategories]);

  // --- Save handler ---
  const handleSave = async (row) => {
    if (!row.transaction_id) return;
    const key = `${row.transaction_type}-${row.transaction_id}`;
    const values = editValues[key];
    if (!values) return;

    const payload = {};
    const originalAmount = Math.abs(Number(row.transaction_amt ?? row.balance_amt ?? 0));

    if (values.amount !== "" && Number(values.amount) !== originalAmount) {
      if (row.transaction_type === "income") payload.income_amt = Number(values.amount);
      else payload.expense_amt = Number(values.amount);
    }

    if ((values.description ?? "") !== (row.description ?? "")) {
      if (row.transaction_type === "income") payload.income_description = values.description;
      else payload.expense_description = values.description;
    }

    if (values.categoryId) {
      if (row.transaction_type === "income") payload.id_income_category = Number(values.categoryId);
      else payload.id_expense_category = Number(values.categoryId);
    }

    if (!Object.keys(payload).length) {
      setSavedMap(s => ({ ...s, [key]: "No changes" }));
      setTimeout(() => setSavedMap(s => ({ ...s, [key]: null })), 1200);
      return;
    }

    setSavingMap(s => ({ ...s, [key]: true }));
    setErrorMap(e => ({ ...e, [key]: null }));

    try {
      if (row.transaction_type === "income") await updateIncome(row.transaction_id, payload);
      else await updateExpense(row.transaction_id, payload);

      setPage(0);
      await fetchAndSetHistory(false, 0);

      setSavedMap(s => ({ ...s, [key]: "Saved" }));
      setTimeout(() => setSavedMap(s => ({ ...s, [key]: null })), 1400);
    } catch (err) {
      console.error("save error", err);
      const msg = err?.response?.data?.detail ?? err?.message ?? "Save failed";
      setErrorMap(e => ({ ...e, [key]: String(msg) }));
    } finally {
      setSavingMap(s => ({ ...s, [key]: false }));
    }
  };

  // --- Delete handler ---
  const handleDelete = async (row) => {
    if (!row.transaction_id) return;
    const key = `${row.transaction_type}-${row.transaction_id}`;
    setSavingMap(s => ({ ...s, [key]: true }));
    setErrorMap(e => ({ ...e, [key]: null }));

    try {
      if (row.transaction_type === "income") await deleteIncome(row.transaction_id);
      else await deleteExpense(row.transaction_id);

      setPage(0);
      await fetchAndSetHistory(false, 0);
      setExpandedRowId(null);
    } catch (err) {
      console.error("delete error", err);
      const msg = err?.response?.data?.detail ?? err?.message ?? "Delete failed";
      setErrorMap(e => ({ ...e, [key]: String(msg) }));
    } finally {
      setSavingMap(s => ({ ...s, [key]: false }));
    }
  };

  // --- CSV Download handler ---
  // Reuses the existing fetchBalanceHistory service (correct base URL, no CORS issues)
  // with a very large limit to fetch all matching records in one shot.
  const handleDownloadCSV = async () => {
    setCsvLoading(true);
    try {
      const rows = await fetchBalanceHistory(startDate, endDate, selectedCategories, 1000000, 0);

      if (!Array.isArray(rows) || rows.length === 0) {
        alert("No records to export.");
        return;
      }

      const headers = ["Date", "Type", "Category", "Amount", "Balance", "Description"];
      const escapeCell = (val) => {
        const str = val == null ? "" : String(val);
        return str.includes(",") || str.includes('"') || str.includes("\n")
          ? `"${str.replace(/"/g, '""')}"`
          : str;
      };

      const csvLines = [
        headers.join(","),
        ...rows.map((item) => {
          const date = new Date(item.created_at + "Z").toLocaleDateString("en-US", {
            timeZone: "America/Chicago",
            year: "numeric",
            month: "short",
            day: "numeric",
          });
          const type = item.transaction_type ?? "";
          const category = item.category ?? "";
          const amount =
            item.transaction_amt != null
              ? (item.transaction_amt >= 0 ? "+" : "") + Number(item.transaction_amt).toFixed(2)
              : "";
          const balance = Number(item.balance_amt).toFixed(2);
          const description = item.description ?? "";
          return [date, type, category, amount, balance, description].map(escapeCell).join(",");
        }),
      ];

      const csvContent = csvLines.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const fileName = `balance_history${startDate ? `_from_${startDate}` : ""}${endDate ? `_to_${endDate}` : ""}.csv`;
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("CSV export error:", err);
      alert("Failed to export CSV. Please try again.");
    } finally {
      setCsvLoading(false);
    }
  };

  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <Layout>
      <div className="min-h-screen p-4 sm:p-6 pb-16 sm:pb-20 relative" style={{
        minHeight: "100svh",
        padding: "max(1rem, env(safe-area-inset-top)) 1rem max(1rem, env(safe-area-inset-bottom)) 1rem",
        boxSizing: "border-box",
      }}>
        <div className="w-full flex items-center justify-between relative">
          <Header />
        </div>
        {/* Left-side back arrow */}
        <div className="w-full flex justify-start mt-6 sm:mt-4 pl-6">
          <Link
            to="/budget"
            className="group flex items-center gap-2 text-blue-400 hover:text-blue-300 
                 font-[Poppins] transition-all duration-300 opacity-0 -translate-x-5 animate-slide-in"
          >
            <ArrowRight
              size={22}
              className="rotate-180 transition-transform duration-300 group-hover:-translate-x-1"
            />
            Back to Budget
          </Link>
        </div>

        <h1 className="hidden md:block text-3xl md:text-4xl font-extrabold text-white mb-6 mt-10 sm:mt-16 text-center">
          Balance History
        </h1>

        <div className="flex flex-row items-center gap-3 sm:gap-4 mb-8 w-full sm:w-auto mt-6 md:mt-0 justify-center">
        </div>

        {/* Graph */}
        <motion.div
          className="w-full max-w-full sm:max-w-3xl mx-auto mb-6 sm:mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-semibold text-gray-300">Balance Trend ({year})</h2>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="bg-gray-800 text-gray-200 px-3 py-1 rounded-md focus:ring-2 focus:ring-indigo-400"
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <div className="w-full h-56 sm:h-64 bg-gray-900 rounded-xl p-2 sm:p-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyTrend} margin={{ top: 10, right: 20, left: 40, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                <XAxis
                  dataKey="month"
                  stroke="#ccc"
                  tickFormatter={(m) => ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1]}
                />
                <YAxis stroke="#ccc" />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="ending_balance"
                  stroke="#34D399"
                  strokeWidth={2.5}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Filters */}
        <div className="max-w-full mx-auto mb-6 relative flex flex-col sm:flex-col md:flex-row justify-center items-center gap-3 sm:gap-4">
          {/* Date pickers row */}
          <div className="flex flex-row justify-center gap-2 sm:gap-4 w-full sm:w-auto">
            <ReactDatePicker
              selected={startDate ? new Date(startDate + 'T00:00:00') : null}
              onChange={(date) =>
                setStartDate(date ? date.toISOString().split("T")[0] : "")
              }
              placeholderText="Start Date"
              className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg bg-gray-800 text-gray-200 border border-gray-700
                 focus:outline-none focus:ring-2 focus:ring-blue-500 hover:bg-gray-700
                 transition-colors duration-200 text-sm sm:text-base w-36 sm:w-40"
              calendarClassName="!bg-gray-800 !text-gray-100 !border-gray-700"
              dayClassName={() => "!text-gray-300 hover:!bg-indigo-500 hover:!text-white transition"}
              popperClassName="!z-50"
            />
            <ReactDatePicker
              selected={endDate ? new Date(endDate + 'T00:00:00') : null}
              onChange={(date) =>
                setEndDate(date ? date.toISOString().split("T")[0] : "")
              }
              placeholderText="End Date"
              className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg bg-gray-800 text-gray-200 border border-gray-700
                 focus:outline-none focus:ring-2 focus:ring-blue-500 hover:bg-gray-700
                 transition-colors duration-200 text-sm sm:text-base w-36 sm:w-40"
              calendarClassName="!bg-gray-800 !text-gray-100 !border-gray-700"
              dayClassName={() => "!text-gray-300 hover:!bg-indigo-500 hover:!text-white transition"}
              popperClassName="!z-50"
            />
          </div>

          {/* Category dropdown */}
          <div className="relative mt-2 sm:mt-0 flex justify-center w-full sm:w-auto" ref={dropdownRef}>
            <button
              onClick={() => {
                setDropdownOpen((p) => !p);
                setTempCategories(selectedCategories);
              }}
              className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg bg-gray-800 text-gray-400 border border-gray-700 
                 hover:bg-gray-700 hover:text-gray-200 transition-all duration-200
                 placeholder-opacity-50 focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
            >
              Select Categories
            </button>

            {dropdownOpen && (
              <div className="absolute mt-2 w-[90vw] sm:w-72 bg-gray-800/95 backdrop-blur-md border border-gray-700 
                             rounded-xl shadow-2xl z-50 overflow-hidden left-1/2 -translate-x-1/2 sm:left-0 sm:translate-x-0">
                <div className="px-4 py-3 bg-gray-900/50 border-b border-gray-700 flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-300">Filter by Category</p>
                  <button
                    onClick={() => setDropdownOpen(false)}
                    className="p-1 hover:bg-gray-700 rounded-md transition-colors"
                    aria-label="Close"
                  >
                    <svg className="w-5 h-5 text-gray-400 hover:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="relative">
                  <div className="max-h-64 sm:max-h-56 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent">
                    {combinedCategoryNames.map((name) => {
                      const isSelected = tempCategories.includes(name);
                      return (
                        <div
                          key={name}
                          onClick={() => toggleTempCategory(name)}
                          className={`flex items-center justify-between p-4 px-4 cursor-pointer
                            transition-all duration-200 border-l-2
                            ${isSelected
                              ? "bg-indigo-500/10 border-indigo-500 text-white"
                              : "border-transparent hover:bg-gray-700/50 text-gray-300 hover:text-white"
                            }`}
                        >
                          <span className="text-sm font-medium">{name}</span>
                          <div className={`w-6 h-6 sm:w-5 sm:h-5 rounded-md border-2 flex items-center justify-center transition-all
                            ${isSelected ? "bg-indigo-500 border-indigo-500" : "border-gray-600 bg-gray-700/30"}`}
                          >
                            {isSelected && (
                              <svg className="w-4 h-4 sm:w-3 sm:h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-gray-800 to-transparent pointer-events-none" />
                </div>

                <div className="flex justify-between items-center gap-2 px-4 py-3 bg-gray-900/50 border-t border-gray-700">
                  <span className="text-xs text-gray-400">{tempCategories.length} selected</span>
                  <button
                    onClick={applyCategories}
                    className="px-5 py-2 sm:px-4 sm:py-1.5 rounded-lg text-sm font-semibold
                               bg-gradient-to-r from-indigo-500 to-purple-600
                               hover:from-indigo-600 hover:to-purple-700
                               text-white shadow-lg hover:shadow-indigo-500/50
                               transition-all duration-300 active:scale-95"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Clear filters button */}
          <div className="mt-2 sm:mt-0">
            <AnimatePresence>
              {(startDate || endDate || selectedCategories.length > 0) && (
                <motion.button
                  key="clear-filters"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.6 }}
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                    setSelectedCategories([]);
                    setTempCategories([]);
                  }}
                  className="px-5 py-2.5 sm:px-4 sm:py-2 rounded-lg text-sm font-semibold text-rose-400 border border-rose-400/70
                             bg-transparent backdrop-blur-sm hover:bg-rose-400/10 hover:text-rose-300 
                             hover:border-rose-400 hover:shadow-[0_0_20px_rgba(244,63,94,0.45)]
                             active:scale-95 focus:outline-none focus:ring-2 focus:ring-rose-500/50 
                             transition-all duration-300"
                >
                  Clear Filters
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Download CSV button — icon only */}
        <div className="max-w-3xl mx-auto mb-4 flex justify-end">
          <button
            onClick={handleDownloadCSV}
            disabled={csvLoading}
            title="Download CSV"
            className="p-2 rounded-lg
                       text-emerald-400 border border-emerald-400/70
                       bg-transparent hover:bg-emerald-400/10 hover:text-emerald-300
                       hover:border-emerald-400 hover:shadow-[0_0_20px_rgba(52,211,153,0.35)]
                       disabled:opacity-50 disabled:cursor-not-allowed
                       active:scale-95 focus:outline-none focus:ring-2 focus:ring-emerald-500/50
                       transition-all duration-300"
          >
            <Download size={18} className={csvLoading ? "animate-bounce" : ""} />
          </button>
        </div>

        {/* History results */}
        <div className="max-w-3xl mx-auto space-y-4">
          {loading && showSkeleton ? (
            [...Array(5)].map((_, idx) => (
              <div key={idx} className="bg-gray-800 p-4 rounded-xl shadow-md animate-pulse">
                <div className="flex justify-between items-center">
                  <div className="h-4 bg-gray-600 rounded w-24" />
                  <div className="h-4 bg-gray-600 rounded w-16" />
                </div>
                <div className="mt-2">
                  <div className="h-3 bg-gray-700 rounded w-20 mb-2" />
                  <div className="h-3 bg-gray-700 rounded w-40" />
                </div>
              </div>
            ))
          ) : !loading && (!history || history.length === 0) ? (
            <p className="text-center text-gray-400">No history available</p>
          ) : (
            <AnimatePresence>
              {history.map((item, idx) => {
                const key = `${item.transaction_type}-${item.transaction_id ?? `tmp-${idx}`}`;
                return (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    className="bg-gray-800 p-4 rounded-xl shadow-md hover:bg-gray-700 transition cursor-pointer"
                    onClick={() => setExpandedRowId(expandedRowId === key ? null : key)}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">
                        {new Date(item.created_at + 'Z').toLocaleDateString('en-US', {
                          timeZone: 'America/Chicago',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                      <span className="font-semibold">{currencyFormatter.format(item.balance_amt)}</span>
                      <span className={`font-semibold ${item.transaction_amt >= 0 ? "text-green-400" : "text-red-400"}`}>
                        {item.transaction_amt >= 0 ? "+" : "-"}{currencyFormatter.format(Math.abs(item.transaction_amt))}
                      </span>
                    </div>
                    <div className="mt-2 text-gray-300">
                      <p className="text-sm">{item.category}</p>
                      {item.description && <p className="text-xs text-gray-400 italic">{item.description}</p>}
                    </div>
                    <AnimatePresence>
                      {expandedRowId === key && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.25 }}
                          className="mt-4 p-4 bg-gray-900/50 backdrop-blur-sm rounded-lg border border-gray-700"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-700">
                            <div>
                              <p className="text-sm text-gray-400">
                                <span className="font-semibold text-gray-300">Type:</span>{" "}
                                <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${item.transaction_type === "income"
                                  ? "bg-green-500/20 text-green-400"
                                  : "bg-red-500/20 text-red-400"
                                  }`}>
                                  {item.transaction_type}
                                </span>
                              </p>
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); setExpandedRowId(null); }}
                              className="p-1.5 hover:bg-gray-700 rounded-md transition-colors"
                              aria-label="Close"
                            >
                              <svg className="w-5 h-5 text-gray-400 hover:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>

                          <div className="space-y-4">
                            <label className="block">
                              <span className="text-sm font-semibold text-gray-300 mb-1.5 block">Amount</span>
                              <input
                                type="number"
                                value={editValues[key]?.amount ?? ""}
                                onChange={(e) => setEditValues(prev => ({
                                  ...prev,
                                  [key]: { ...prev[key], amount: e.target.value === "" ? "" : Number(e.target.value) },
                                }))}
                                className="w-full px-3 py-2 rounded-lg bg-gray-700 text-white border border-gray-600
                                           focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                                           transition-all duration-200"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </label>

                            <label className="block">
                              <span className="text-sm font-semibold text-gray-300 mb-1.5 block">Category</span>
                              <select
                                value={editValues[key]?.categoryId ?? ""}
                                onChange={(e) => setEditValues(prev => ({
                                  ...prev,
                                  [key]: { ...prev[key], categoryId: e.target.value },
                                }))}
                                className="w-full px-3 py-2 rounded-lg bg-gray-700 text-white border border-gray-600
                                           focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                                           transition-all duration-200 cursor-pointer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <option value="" className="bg-gray-800">(Keep current)</option>
                                {categoriesForType(item.transaction_type).map((cat) => (
                                  <option key={cat.id} value={cat.id} className="bg-gray-800">{cat.name}</option>
                                ))}
                              </select>
                            </label>

                            <label className="block">
                              <span className="text-sm font-semibold text-gray-300 mb-1.5 block">Description</span>
                              <input
                                type="text"
                                value={editValues[key]?.description ?? ""}
                                onChange={(e) => setEditValues(prev => ({
                                  ...prev,
                                  [key]: { ...prev[key], description: e.target.value },
                                }))}
                                className="w-full px-3 py-2 rounded-lg bg-gray-700 text-white border border-gray-600
                                           focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                                           transition-all duration-200"
                                placeholder="Optional description"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </label>

                            <div className="flex gap-3 pt-2">
                              <button
                                disabled={savingMap[key]}
                                onClick={() => handleSave(item)}
                                className="flex-1 px-4 py-2.5 rounded-lg font-semibold text-sm
                                           bg-gradient-to-r from-green-500 to-emerald-600
                                           hover:from-green-600 hover:to-emerald-700
                                           disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed
                                           text-white shadow-lg hover:shadow-green-500/50
                                           transition-all duration-300 active:scale-95"
                              >
                                {savingMap[key] ? "Saving…" : "Save Changes"}
                              </button>
                              <button
                                disabled={savingMap[key]}
                                onClick={() => handleDelete(item)}
                                className="flex-1 px-4 py-2.5 rounded-lg font-semibold text-sm
                                           bg-gradient-to-r from-red-500 to-rose-600
                                           hover:from-red-600 hover:to-rose-700
                                           disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed
                                           text-white shadow-lg hover:shadow-red-500/50
                                           transition-all duration-300 active:scale-95"
                              >
                                {savingMap[key] ? "Deleting…" : "Delete"}
                              </button>
                            </div>

                            {errorMap[key] && (
                              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                                <p className="text-red-400 text-sm font-medium">{errorMap[key]}</p>
                              </div>
                            )}
                            {savedMap[key] && (
                              <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                                <p className="text-green-400 text-sm font-medium">✓ {savedMap[key]}</p>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
        </div>

        {/* Infinite scroll loader */}
        <div ref={loaderRef} className="mt-6 text-center text-gray-400 text-sm h-8 flex items-center justify-center">
          {loading ? "Loading..." : hasMore ? "Scroll for more…" : "End of records"}
        </div>
      </div>
    </Layout>
  );
}